import { useEffect, useMemo, useState } from 'react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko } from '../layout/ListFiltr';
import { ZNAMKA_COLORS } from '../flotila/ScoreCircle';
import KartaRidice from './KartaRidice';
import { SECTION_COLOR, SECTION_BG, nacti, cislo } from './ui';

/**
 * Rozvoz → Řidiči. Norma §1: seznam řidičů seřazený podle skóre (široký =
 * mřížka se skóre, km, jízdami a body na km) · karta řidiče jako detail ve
 * sloupci (dřív samostatná stránka) · třetí sloupec `panelVzdy` s čísly týmu
 * za zvolené období.
 */
const POHLEDY = [
    { id: 'vse', label: 'Všichni', patri: () => true },
    { id: 'jezdi', label: 'S jízdami', patri: (r) => (r.km_celkem ?? 0) > 0 },
    { id: 'bez', label: 'Bez jízd', patri: (r) => !(r.km_celkem > 0) },
];
const OBDOBI = [
    { value: '7', label: 'Posledních 7 dní' },
    { value: '30', label: 'Posledních 30 dní' },
    { value: '90', label: 'Posledních 90 dní' },
    { value: '365', label: 'Posledních 12 měsíců' },
];

const jmeno = (r) => r.cele_jmeno || `${r.jmeno || ''} ${r.prijmeni || ''}`.trim();
const bezDat = (r) => r.body_per_km == null || r.znamka === '—' || !r.znamka;

export default function RozvozRidici({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritId = null }) {
    const [obdobi, setObdobi] = useState('30');
    const [ridici, setRidici] = useState(null);
    const [kpi, setKpi] = useState(null);
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(otevritId);
    useEffect(() => { if (otevritId) setVybranyId(otevritId); }, [otevritId]);

    useEffect(() => {
        nacti(`/api/flotila/ridici?obdobi=${obdobi}`).then((d) => setRidici(Array.isArray(d) ? d : [])).catch(() => setRidici([]));
        nacti(`/api/flotila/ridici-kpi?obdobi=${obdobi}`).then(setKpi).catch(() => setKpi(null));
    }, [obdobi]);

    const vse = useMemo(() => [...(ridici || [])].sort((a, b) => {
        if (bezDat(a) !== bezDat(b)) return bezDat(a) ? 1 : -1;
        return (b.score_pct ?? -1) - (a.score_pct ?? -1);
    }), [ridici]);
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map((p) => [p.id, vse.filter(p.patri).length])), [vse]);
    const akt = POHLEDY.find((p) => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const seznam = vse.filter((r) => akt.patri(r) && (!q || jmeno(r).toLowerCase().includes(q)));
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    const filtrSkupiny = [{ id: 'obdobi', label: 'Období', typ: 'dropdown', options: OBDOBI, vychozi: '30' }];

    const radek = (r, siroky, poradi) => {
        const sel = String(r.zamestnanec_id) === String(vybranyId);
        const barva = bezDat(r) ? muted : (ZNAMKA_COLORS[r.znamka] || 'var(--text-primary)');
        return (
            <button key={r.zamestnanec_id} type="button" onClick={() => setVybranyId(r.zamestnanec_id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, cursor: 'pointer', border: 'none', borderBottom: `1px solid ${cara}`,
                    background: sel ? SECTION_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', opacity: bezDat(r) ? 0.65 : 1 }}>
                <span style={{ width: 22, textAlign: 'right', fontSize: 12, fontWeight: 700, color: muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{bezDat(r) ? '' : poradi}</span>
                <span style={{ width: 32, height: 32, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: SECTION_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: SECTION_COLOR }}>
                    {r.foto_url ? <img src={r.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : `${(r.jmeno || '?')[0]}${(r.prijmeni || '')[0] || ''}`}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{jmeno(r)}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>
                        {siroky ? (r.kariera_uroven_nazev || r.role_nazev || '') : [`${cislo(r.km_celkem)} km`, `${r.pocet_jizd ?? 0} jízd`].join(' · ')}
                    </p>
                </div>
                {siroky && <span style={{ width: 90, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{cislo(r.km_celkem)} km</span>}
                {siroky && <span style={{ width: 70, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{r.pocet_jizd ?? 0}</span>}
                {siroky && <span style={{ width: 80, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: muted }}>{r.body_per_km != null ? Number(r.body_per_km).toFixed(2) : '—'}</span>}
                <span className="inline-flex items-center justify-end" style={{ width: siroky ? 90 : 'auto', flexShrink: 0, gap: 6, fontWeight: 700, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
                    {!bezDat(r) && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 99, background: barva }} />}
                    {bezDat(r) ? '—' : (r.score_pct != null ? `${Math.round(r.score_pct)} %` : '—')}
                </span>
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat řidiče…', placeholderKratky: 'Hledat' }}
                filtr={(
                    <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={{ obdobi }} onChange={(id, h) => setObdobi(h || '30')} onReset={() => setObdobi('30')}
                        pocetVysledku={seznam.length} siroky={siroky} kompaktni={isMobile} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                )}
                pohled={POHLEDY.map((p) => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))} />
            {siroky && (
                <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                    <span style={{ width: 22 }} /><span style={{ width: 32 }} />
                    <span className="flex-1">Řidič · {OBDOBI.find((o) => o.value === obdobi)?.label.toLowerCase()}</span>
                    <span style={{ width: 90, textAlign: 'right' }}>Km</span>
                    <span style={{ width: 70, textAlign: 'right' }}>Jízd</span>
                    <span style={{ width: 80, textAlign: 'right' }}>Body/km</span>
                    <span style={{ width: 90, textAlign: 'right' }}>Skóre</span>
                </div>
            )}
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {ridici === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {ridici && seznam.length === 0 && <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>{q ? 'Nic se neshoduje.' : 'Žádní řidiči v tomhle pohledu.'}</p>}
                {seznam.map((r) => radek(r, siroky, vse.filter((x) => !bezDat(x)).indexOf(r) + 1))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => (
        <div className="flex flex-col flex-1 min-h-0" style={{ overflowY: 'auto', padding: isMobile ? '6px 14px 0' : 0 }}>
            <KartaRidice zamestnanecId={vybranyId} onVyberRidice={setVybranyId} onZpet={isMobile ? () => setVybranyId(null) : null} />
            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
        </div>
    );

    const panel = () => {
        const r = (l, h) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{h}</b>
            </div>
        );
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 6px' }}>
                    Tým · {OBDOBI.find((o) => o.value === obdobi)?.label.toLowerCase()}
                </p>
                {r('Řidičů s jízdami', kpi?.aktivnich_ridicu ?? '—')}
                {r('Průměrné skóre', kpi?.prumer_score_pct != null ? `${kpi.prumer_score_pct} %` : '—')}
                {r('Body na km', kpi?.prumer_body_per_km != null ? Number(kpi.prumer_body_per_km).toFixed(2) : '—')}
                {r('Ujeto', `${cislo(kpi?.total_km)} km`)}
                {r('Jízd', kpi?.total_jizd ?? '—')}
                <p className="text-xs" style={{ color: muted, margin: '12px 0 0' }}>
                    Skóre počítá brzdění, akceleraci, zatáčení a rychlost na 100 km. Seznam je seřazený od nejlepšího.
                </p>
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
        </div>
    );
}
